<script setup lang="ts">
import {ref, computed, onUnmounted, onMounted} from 'vue';
import { useI18n } from 'vue-i18n';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import {Navigation, Pagination} from 'swiper/modules';

const props = defineProps({
  images: { type: Array, required: true },
  isMobile: { type: Boolean, default: false }
});


const emit = defineEmits(['openGallery']); // Event when user clicks to open modal
const { t } = useI18n();

// Reactive window width
const windowWidth = ref(window.innerWidth);

const updateWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => window.addEventListener('resize', updateWidth));
onUnmounted(() => window.removeEventListener('resize', updateWidth));

// Compute how many images to show on desktop based on window width
const showImagesCount = computed(() => {
  if (windowWidth.value <= 420) return 1;
  if (windowWidth.value <= 580) return 2;
  if (windowWidth.value <= 1024) return 3;
  return 3; // default for larger screens
});
</script>

<template>
  <section id="gallery">
    <div class="bg-primary-darkBlue">
      <div class="container py-12">
        <span class="gallery-title font-semibold responsive-title-4 text-white">
          {{ t('gallery') }}
        </span>

        <!-- Desktop -->
        <div v-if="!isMobile" class="gallery-content flex gap-6 relative py-5">
          <div
              v-for="(image, index) in images.slice(0, showImagesCount)"
              :key="index"
              class="image-container flex flex-col gap-2 w-2/5"
          >
            <img
                @click="$emit('openGallery', index)"
                :src="image.url"
                class="cursor-pointer rounded-8xl imageCol"
            />
          </div>

          <button
              v-if="images.length > showImagesCount"
              @click="$emit('openGallery')"
              class="show-gallery-photos-btn transition-all absolute bottom-8 right-2 bg-white text-black responsive-body-medium px-4 py-2 rounded-12xl font-semibold"
          >
            {{ t('showAllPhotos', { count: images.length - showImagesCount }) }}
          </button>
        </div>

        <!-- Mobile Swiper -->
        <Swiper
            v-else
            class="py-5"
            :modules="[Navigation, Pagination]"
            :slides-per-view="1"
            :space-between="10"
            :loop="true"
            :navigation="true"
            :pagination="{ clickable: true }"
        >
          <SwiperSlide v-for="(image, index) in images" :key="index">
            <img
                :src="image.url"
                class="cursor-pointer rounded-8xl w-full imageCol"
                @click="$emit('openGallery', index)"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </section>

  <!-- Included Charges Section -->
  <div class="container addons-container mt-12 flex flex-col gap-5">
    <div class="charges-title responsive-title-4 font-semibold">{{ t('includedCharges') }}</div>
    <div class="charges-subtitle responsive-body-large text-bw-darkGrey">{{ t('includedChargesText') }}</div>
    <div class="included-charges flex flex-col sm:grid sm:grid-cols-3 gap-5 align-center">
      <div class="box-border flex align-center w-full h-100 p-5 gap-2 md:gap-0 lg:gap-6">
        <v-checkbox
            :model-value="true"
            :ripple="false"
            class="always-enabled"
            disabled
            base-color="#1B84FF"
        />
        <img src="@/assets/home/svg/taxes.svg" alt="taxes" class="w-13 h-13"/>
        <div class="flex flex-col justify-between mt-auto mb-auto">
          <div class="flex">
            <div class="included-charges-title responsive-body-large font-semibold">{{ t('taxes') }}</div>
          </div>
        </div>
      </div>

      <div class="box-border align-center flex w-full h-100 p-5 gap-2 md:gap-0 lg:gap-6">
        <v-checkbox
            :model-value="true"
            :ripple="false"
            class="always-enabled"
            disabled
            base-color="#1B84FF"
        />
        <img src="@/assets/home/svg/insurance.svg" alt="insurance" class="w-13 h-13"/>
        <div class="flex flex-col justify-between mt-auto mb-auto">
          <div class="flex flex-col">
            <span class="included-charges-title responsive-body-large font-semibold">{{ t('insurance') }}</span>
          </div>
        </div>
      </div>

      <div class="box-border align-center flex w-full h-100 p-5 gap-2 md:gap-0 lg:gap-6">
        <v-checkbox
            :model-value="true"
            :ripple="false"
            class="always-enabled"
            disabled
            base-color="#1B84FF"
        />
        <img src="@/assets/home/svg/support.svg" alt="support" class="w-13 h-13"/>
        <div class="flex flex-col justify-between mt-auto mb-auto">
          <div class="flex flex-col">
            <span class="included-charges-title responsive-body-large font-semibold">{{ t('support') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  
</template>

<style scoped>
@media (max-width: 768px) {
  #gallery {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }

  #gallery .gallery-title {
    font-size: 1.25rem;
  }

  #gallery .gallery-content {
    padding-top: .875rem !important;
    padding-bottom: .875rem !important;
  }

  .addons-container {
    margin-top: 2.5rem !important;
    row-gap: .875rem;
  }

  .charges-title {
    font-size: 1.25rem;
  }
  
}


.box-border {
  border-radius: 10px;
  border: 2px solid #1B84FF;
}

@media (max-width: 580px) {
  .image-container {
    width: 50% !important;
  }

  .charges-subtitle {
    font-size: .875rem;
  }
}

@media (max-width: 420px) {
  .image-container {
    width: 100% !important;
  }
}

@media (max-width: 1538px) {
  .show-gallery-photos-btn {
    font-size: .875rem;
  }
  .charges-title {
    font-size: 1.5rem;
  }

  .charges-subtitle {
    font-size: 1rem;
  }

  .included-charges-title {
    font-size: 1.125rem;
  }

}

.imageCol {
  height: 260px;
  object-fit: cover;
}

.show-gallery-photos-btn:hover {
  background-color: black !important;
  color: #fff !important;
}



</style>
